import { useState, useEffect } from 'react';
import { useUserData } from '../hooks/useUserData';
import Logo from '../components/Logo';
import { BanknotesIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserCircleIcon,
  CogIcon,
  CurrencyEuroIcon,
  ChartPieIcon,
  BuildingOfficeIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  QrCodeIcon,
  WalletIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { usePaymentSettings } from '../firebase/paymentSettings';
import { QRCodeSVG } from 'qrcode.react';
import CryptoMarketData from '../components/CryptoMarketData';
import PortfolioPerformance from '../components/PortfolioPerformance';
import CryptoWidgets from '../components/CryptoWidgets';
import CryptoPriceTicker from '../components/CryptoPriceTicker';
import TradingViewWidget from '../components/TradingViewWidget';
import CryptoPriceWidget from '../components/CryptoPriceWidget';
import { useAuth } from '../contexts/AuthContext';
import OTPVerification from '../components/OTPVerification';
import { addDoc, collection, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';

// Add this helper function at the top of the file, outside the component
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''}`
  } else if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''}`
  } else {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`
  }
};

function UserDashboard() {
  const { userData, loading, error } = useUserData();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notifications, setNotifications] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('crypto');
  const [withdrawMethod, setWithdrawMethod] = useState('crypto');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const [depositMethods, setDepositMethods] = useState({
    crypto: {
      title: "Cryptocurrency",
      description: "Fast and secure crypto deposits",
      methods: []
    },
    cashapp: {
      title: "CashApp",
      description: "Quick and easy mobile payments",
      cashtag: "",
      instructions: []
    },
    paypal: {
      title: "PayPal",
      description: "Safe and instant transfers",
      email: "",
      instructions: []
    },
    bank: {
      title: "Bank Transfer",
      description: "Traditional bank wire transfer",
      details: {}
    }
  });

  const cryptoData = [
    { name: 'BTC', value: 45000, change: '+5.2%' },
    { name: 'ETH', value: 3200, change: '+3.8%' },
    { name: 'SOL', value: 120, change: '-2.1%' },
  ];

  const portfolioData = [
    { name: 'Stocks', value: 40 },
    { name: 'Crypto', value: 30 },
    { name: 'Real Estate', value: 20 },
    { name: 'Cash', value: 10 },
  ];

  const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B'];

  const performanceData = [
    { month: '2024-01-01', value: 4000, profit: 2400 },
    { month: '2024-02-01', value: 3000, profit: 1398 },
    { month: '2024-03-01', value: 5000, profit: 3800 },
    { month: '2024-04-01', value: 2780, profit: 3908 },
    { month: '2024-05-01', value: 1890, profit: 4800 },
    { month: '2024-06-01', value: 2390, profit: 3800 },
  ];

  const PORTFOLIO_ITEMS = [
    { 
      name: 'Stocks', 
      key: 'stocks',
      icon: ArrowTrendingUpIcon, 
      color: '#10B981' // Green
    },
    { 
      name: 'Crypto', 
      key: 'crypto',
      icon: CircleStackIcon, 
      color: '#F59E0B' // Amber
    },
    { 
      name: 'Real Estate', 
      key: 'realEstate',
      icon: BuildingOfficeIcon, 
      color: '#3B82F6' // Blue
    },
    { 
      name: 'Cash', 
      key: 'cash',
      icon: CurrencyDollarIcon, 
      color: '#EC4899' // Pink
    }
  ];

  const withdrawMethods = {
    crypto: {
      title: "Cryptocurrency",
      description: "Fast crypto withdrawals",
      options: [
        { name: "Bitcoin (BTC)", fee: "0.0005 BTC" },
        { name: "Ethereum (ETH)", fee: "0.01 ETH" },
        { name: "USDT (TRC20)", fee: "1 USDT" }
      ]
    },
    bank: {
      title: "Bank Transfer",
      description: "2-5 business days",
      fee: "$25 wire fee",
      requirements: [
        "Bank Name",
        "Account Number",
        "Routing Number",
        "Account Holder Name",
        "Bank Address"
      ]
    },
    cashapp: {
      title: "CashApp",
      description: "Instant withdrawals",
      fee: "1.5%",
      limit: "$7,500 per week"
    },
    paypal: {
      title: "PayPal",
      description: "Instant withdrawals",
      fee: "2.9% + $0.30",
      limit: "$10,000 per transaction"
    }
  };

  const paymentSettings = usePaymentSettings();

  useEffect(() => {
    if (paymentSettings) {
      setDepositMethods({
        crypto: {
          title: "Cryptocurrency",
          description: "Fast and secure crypto deposits",
          methods: [
            {
              name: paymentSettings.crypto.bitcoin.name,
              address: paymentSettings.crypto.bitcoin.address
            },
            {
              name: paymentSettings.crypto.ethereum.name,
              address: paymentSettings.crypto.ethereum.address
            },
            {
              name: paymentSettings.crypto.usdt.name,
              address: paymentSettings.crypto.usdt.address
            }
          ]
        },
        cashapp: {
          title: "CashApp",
          description: "Quick and easy mobile payments",
          cashtag: paymentSettings.cashapp.cashtag,
          instructions: paymentSettings.cashapp.instructions
        },
        paypal: {
          title: "PayPal",
          description: "Safe and instant transfers",
          email: paymentSettings.paypal.email,
          instructions: paymentSettings.paypal.instructions
        },
        bank: {
          title: "Bank Transfer",
          description: "Traditional bank wire transfer",
          details: paymentSettings.bank.details
        }
      });
    }
  }, [paymentSettings]);

  const TopNav = () => (
    <nav className="lg:hidden bg-white shadow-lg fixed w-full top-0 z-50">
      <div className="px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Logo darkMode={false} />
          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - Positioned below the header */}
      <div className={`${mobileMenuOpen ? 'block' : 'hidden'} absolute w-full bg-white shadow-lg`}>
        <div className="pt-2 pb-3 space-y-1">
          <MobileNavLink 
            icon={<HomeIcon className="w-5 h-5" />} 
            text="Dashboard" 
            active={activeTab === 'dashboard'}
            onClick={() => {
              setActiveTab('dashboard');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<ArrowUpCircleIcon className="w-5 h-5" />} 
            text="Deposit" 
            active={activeTab === 'deposit'}
            onClick={() => {
              setActiveTab('deposit');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<ArrowDownCircleIcon className="w-5 h-5" />} 
            text="Withdraw" 
            active={activeTab === 'withdraw'}
            onClick={() => {
              setActiveTab('withdraw');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<BellIcon className="w-5 h-5" />} 
            text="Notifications" 
            active={activeTab === 'notifications'}
            badge={notifications.filter(n => !n.read).length}
            onClick={() => {
              setActiveTab('notifications');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<ArrowRightOnRectangleIcon className="w-5 h-5" />} 
            text="Sign Out" 
            onClick={handleSignOut}
          />
        </div>
      </div>
    </nav>
  );

  const NotificationsPanel = () => (
    <div className={`${activeTab === 'notifications' ? 'block' : 'hidden'} p-6`}>
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="p-4 bg-primary-50">
            <h2 className="text-lg font-semibold text-primary-900">Notifications</h2>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 ${notification.read ? 'bg-white' : 'bg-blue-50'}`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <BellIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {notification.title}
                  </p>
                  <div className="mt-1 text-sm text-gray-500 whitespace-pre-line">
                    {notification.message}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    {new Date(notification.timestamp).toLocaleDateString()} at{' '}
                    {new Date(notification.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          {notifications.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              No notifications yet
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      // Optionally add error notification here
    }
  };

  const Sidebar = () => (
    <div className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-primary-600 to-primary-800 text-white">
      <div className="flex flex-col w-full">
        <div className="p-4 border-b border-primary-500/30">
          <Logo darkMode={true} />
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <SidebarLink 
            icon={<HomeIcon className="w-5 h-5" />} 
            text="Dashboard" 
            active={activeTab === 'dashboard'}
            onClick={() => setActiveTab('dashboard')}
          />
          <SidebarLink 
            icon={<ArrowUpCircleIcon className="w-5 h-5" />} 
            text="Deposit" 
            active={activeTab === 'deposit'}
            onClick={() => setActiveTab('deposit')}
          />
          <SidebarLink 
            icon={<ArrowDownCircleIcon className="w-5 h-5" />} 
            text="Withdraw" 
            active={activeTab === 'withdraw'}
            onClick={() => setActiveTab('withdraw')}
          />
          <SidebarLink 
            icon={<BellIcon className="w-5 h-5" />} 
            text="Notifications" 
            active={activeTab === 'notifications'}
            onClick={() => setActiveTab('notifications')}
            badge={notifications.filter(n => !n.read).length.toString()}
          />
        </nav>
        <div className="p-4 border-t border-primary-500/30">
          <motion.button
            onClick={handleSignOut}
            className="flex items-center space-x-3 p-3 w-full rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </motion.button>
        </div>
      </div>
    </div>
  );

  const SidebarLink = ({ icon, text, active, badge, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-between p-3 rounded-lg transition-colors w-full ${
        active ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5 hover:text-white'
      }`}
    >
      <div className="flex items-center space-x-3">
        {icon}
        <span className="font-medium">{text}</span>
      </div>
      {badge && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );

  const MobileNavLink = ({ icon, text, active, badge, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-between w-full px-4 py-3 text-base font-medium border-l-4 ${
        active
          ? 'border-primary-600 text-primary-600 bg-primary-50'
          : 'border-transparent text-gray-700 hover:text-primary-600 hover:bg-primary-50 hover:border-primary-600'
      }`}
    >
      <div className="flex items-center space-x-3">
        {icon}
        <span>{text}</span>
      </div>
      {badge && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );

  const getPortfolioData = () => {
    if (!userData?.portfolio) return [];
    
    return PORTFOLIO_ITEMS.map(item => ({
      name: item.name,
      value: userData.portfolio[item.key] || 0,
      color: item.color,
      icon: item.icon
    }));
  };

  useEffect(() => {
    if (userData?.name) {
      setNotifications([
        {
          id: 'welcome',
          type: 'welcome',
          title: `Welcome ${userData.name}!`,
          message: `Welcome to QuickwealthInvest! Here's how to get started:\n\n1. Make your first deposit by clicking the 'Deposit' tab\n2. Choose from our investment plans to start growing your portfolio\n3. Track your investments in real-time from your dashboard\n4. Withdraw your profits easily when ready\n\nFor any questions or support, please email support@Quickwealthinvest.com`,
          timestamp: userData.createdAt || new Date(),
          read: false
        }
      ]);
    }
  }, [userData?.name]);

  // Function to add withdrawal notification
  const addWithdrawalNotification = (withdrawalDetails) => {
    setNotifications(prev => [
      {
        id: `withdrawal-${Date.now()}`,
        type: 'withdrawal',
        title: 'Withdrawal Request Submitted',
        message: `Your withdrawal request for $${withdrawalDetails.amount.toLocaleString()} via ${withdrawalDetails.method} has been submitted and is pending approval. You will be notified once it's processed.`,
        timestamp: new Date(),
        read: false
      },
      ...prev
    ]);
  };

  const CryptoAddress = ({ crypto }) => (
    <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
      <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">{crypto.name}</h4>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="w-full sm:w-32 h-32 bg-white rounded-lg p-2 border">
          <QRCodeSVG
            value={crypto.address}
            size={112}
            level="H"
            includeMargin={true}
            className="w-full h-full"
          />
        </div>
        <div className="flex-1 w-full">
          <p className="text-sm text-gray-600 mb-2">Deposit Address:</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={crypto.address}
              readOnly
              className="flex-1 p-2 text-sm bg-gray-50 rounded border break-all"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(crypto.address);
                // Optional: Add toast notification for copy success
              }}
              className="w-full sm:w-auto px-4 py-2 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded font-medium transition-colors"
            >
              Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const DepositPanel = () => (
    <div className="p-2 sm:p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="p-4 bg-primary-50">
            <h2 className="text-lg font-semibold text-primary-900">Deposit Funds</h2>
          </div>
        </div>
        
        {/* Important Notice - Moved to top and enhanced styling */}
        <div className="p-4 sm:p-6">
          <div className="bg-yellow-100 border-l-4 border-yellow-500 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-yellow-800">Important Notice:</h3>
                <ul className="mt-2 list-disc list-inside space-y-1 text-yellow-800">
                  <li className="font-medium">Deposits must be completed within 24 hours of initiation</li>
                  <li className="font-medium">Send proof of payment to your consultant or email to admin@quickwealthinvestment.com</li>
                  <li className="font-medium">Include your account ID in the payment reference/notes for faster processing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {Object.keys(depositMethods).map((method) => (
              <motion.button
                key={method}
                onClick={() => setSelectedPaymentMethod(method)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedPaymentMethod === method 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-primary-200'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{depositMethods[method].title}</h3>
                <p className="text-xs sm:text-sm text-gray-600">{depositMethods[method].description}</p>
              </motion.button>
            ))}
          </div>

          {/* Selected Method Details */}
          <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
            {selectedPaymentMethod === 'crypto' && (
              <div className="space-y-4 sm:space-y-6">
                {depositMethods.crypto.methods.map((crypto) => (
                  <CryptoAddress key={crypto.name} crypto={crypto} />
                ))}
              </div>
            )}

            {selectedPaymentMethod === 'cashapp' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-full sm:w-32 h-32 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl sm:text-4xl font-bold text-primary-600">
                      {depositMethods.cashapp.cashtag}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">CashApp Instructions</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base">
                      {depositMethods.cashapp.instructions.map((instruction, index) => (
                        <li key={index} className="text-gray-600">{instruction}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {selectedPaymentMethod === 'paypal' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-full sm:w-32 h-32 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl sm:text-2xl font-bold text-primary-600 break-all px-2">
                      {depositMethods.paypal.email}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">PayPal Instructions</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base">
                      {depositMethods.paypal.instructions.map((instruction, index) => (
                        <li key={index} className="text-gray-600">{instruction}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {selectedPaymentMethod === 'bank' && (
              <div className="space-y-3 sm:space-y-4">
                <h4 className="text-base sm:text-lg font-semibold">Bank Transfer Details</h4>
                {Object.entries(depositMethods.bank.details).map(([key, value]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-sm sm:text-base text-gray-600 capitalize mb-1 sm:mb-0">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="text-sm sm:text-base font-medium">{value}</span>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Important: Please include your account ID in the transfer description for faster processing.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Processing Time Notice */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Deposits are typically processed within 1-2 business hours.
              <br className="hidden sm:block" />
              Contact support if you need assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const WithdrawPanel = () => {
    const [withdrawMethod, setWithdrawMethod] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawAddress, setWithdrawAddress] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [notifyByEmail, setNotifyByEmail] = useState(true);
    const [userEmail, setUserEmail] = useState(userData?.email || '');
    const [withdrawStatus, setWithdrawStatus] = useState(null);
    const [showOTP, setShowOTP] = useState(false);
    const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
    const { verifyWithdrawalOTP, currentUser } = useAuth();
  
    const handleWithdrawalSubmit = async (e) => {
      e.preventDefault();
      
      // Validation
      if (!withdrawMethod) {
        alert("Please select a withdrawal method");
        return;
      }
      
      if (!withdrawAmount || parseFloat(withdrawAmount) < 100) {
        alert("Please enter a valid amount (minimum $100)");
        return;
      }
  
      // Store withdrawal details and show OTP verification
      setPendingWithdrawal({
        amount: parseFloat(withdrawAmount),
        method: withdrawMethod,
        address: withdrawAddress,
        email: notifyByEmail ? userEmail : null,
        timestamp: new Date().toISOString()
      });
      setShowOTP(true);
    };

    const handleOTPVerify = async (otp) => {
      try {
        setIsProcessing(true);
        await verifyWithdrawalOTP(currentUser.uid, otp);
        
        // Create withdrawal request in Firestore
        const withdrawalRequest = {
          ...pendingWithdrawal,
          userId: currentUser.uid,
          status: 'pending',
          userEmail: currentUser.email,
          timestamp: new Date().toISOString(),
          userDetails: {
            fullName: userData?.fullName || userData?.name || 'Unknown',
            accountLevel: userData?.accountLevel || 'standard',
            country: userData?.country || 'Unknown'
          },
          withdrawalDetails: {
            amount: parseFloat(withdrawAmount),
            method: withdrawMethod,
            address: withdrawAddress || null,
            currency: 'USD'
          }
        };

        // Add withdrawal request to Firestore
        const withdrawalRef = await addDoc(collection(db, 'withdrawalRequests'), withdrawalRequest);
        
        // Update user's pending withdrawals in a separate transaction
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          'accountDetails.pendingWithdrawals': increment(parseFloat(withdrawAmount))
        });

        // Add notification
        addWithdrawalNotification({
          amount: parseFloat(withdrawAmount),
          method: withdrawMethod
        });

        setWithdrawStatus({ 
          type: 'success', 
          message: 'Withdrawal request submitted successfully!'
        });
        
        // Reset form
        setWithdrawAmount('');
        setWithdrawMethod('');
        setWithdrawAddress('');
        setShowOTP(false);
        setPendingWithdrawal(null);
      } catch (err) {
        console.error('Withdrawal error:', err);
        setWithdrawStatus({ 
          type: 'error', 
          message: err.message || 'Failed to process withdrawal request'
        });
      } finally {
        setIsProcessing(false);
      }
    };

    const handleOTPCancel = () => {
      setShowOTP(false);
      setPendingWithdrawal(null);
      setWithdrawStatus(null);
    };
  
    const resetForm = () => {
      setWithdrawAmount('');
      setWithdrawAddress('');
      setWithdrawStatus(null);
      setShowOTP(false);
      setPendingWithdrawal(null);
    };
  
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="p-4 bg-primary-50">
              <h2 className="text-lg font-semibold text-primary-900">Withdraw Funds</h2>
            </div>
          </div>
          
          <div className="p-6">
            {/* Available Balance */}
            <div className="mb-8 bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Balance</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${userData?.balance?.toLocaleString() || '0'}
                  </p>
                </div>
                <BanknotesIcon className="w-8 h-8 text-primary-600" />
              </div>
            </div>

            {/* OTP Verification Modal */}
            {showOTP && (
              <OTPVerification
                type="withdrawal"
                onVerify={handleOTPVerify}
                onCancel={handleOTPCancel}
              />
            )}
  
            {/* Withdrawal Status Alert */}
            {withdrawStatus && (
              <div className={`mb-6 p-4 rounded-lg ${
                withdrawStatus.type === 'processing' ? 'bg-blue-50 text-blue-700' :
                withdrawStatus.type === 'success' ? 'bg-green-50 text-green-700' :
                'bg-red-50 text-red-700'
              }`}>
                <div className="flex items-center">
                  {withdrawStatus.type === 'processing' && (
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {withdrawStatus.type === 'success' && (
                    <CheckCircleIcon className="h-5 w-5 mr-3" />
                  )}
                  {withdrawStatus.type === 'error' && (
                    <ExclamationCircleIcon className="h-5 w-5 mr-3" />
                  )}
                  <p>{withdrawStatus.message}</p>
                </div>
                
                {withdrawStatus.type === 'success' && (
                  <button 
                    onClick={resetForm}
                    className="mt-3 text-sm font-medium underline"
                  >
                    Make another withdrawal
                  </button>
                )}
              </div>
            )}
  
            {/* Withdrawal Method Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {Object.keys(withdrawMethods).map((method) => (
                <motion.button
                  key={method}
                  onClick={() => setWithdrawMethod(method)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    withdrawMethod === method 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 hover:border-primary-200'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isProcessing}
                >
                  <h3 className="text-lg font-semibold mb-2">{withdrawMethods[method].title}</h3>
                  <p className="text-sm text-gray-600">{withdrawMethods[method].description}</p>
                </motion.button>
              ))}
            </div>
  
            {/* Withdrawal Form */}
            <div className="bg-gray-50 rounded-lg p-6">
              <form onSubmit={handleWithdrawalSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Withdrawal Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      className="pl-8 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter amount"
                      min="100"
                      max={userData?.balance || 0}
                      disabled={isProcessing}
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Minimum withdrawal: $100
                  </p>
                </div>
  
                {withdrawMethod === 'crypto' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Cryptocurrency
                    </label>
                    <select 
                      className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={isProcessing}
                    >
                      {withdrawMethods.crypto.options.map((option) => (
                        <option key={option.name}>
                          {option.name} (Fee: {option.fee})
                        </option>
                      ))}
                    </select>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wallet Address
                      </label>
                      <input
                        type="text"
                        value={withdrawAddress}
                        onChange={(e) => setWithdrawAddress(e.target.value)}
                        className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your wallet address"
                        disabled={isProcessing}
                      />
                    </div>
                  </div>
                )}
  
                {withdrawMethod === 'bank' && (
                  <div className="space-y-4">
                    {withdrawMethods.bank.requirements.map((field) => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field}
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder={`Enter ${field.toLowerCase()}`}
                          disabled={isProcessing}
                        />
                      </div>
                    ))}
                    <p className="text-sm text-gray-500">
                      Wire Fee: {withdrawMethods.bank.fee}
                    </p>
                  </div>
                )}
  
                {withdrawMethod === 'cashapp' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CashApp $Cashtag
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your $Cashtag"
                      disabled={isProcessing}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Limit: {withdrawMethods.cashapp.limit}
                      <br />
                      Fee: {withdrawMethods.cashapp.fee}
                    </p>
                  </div>
                )}

                {withdrawMethod === 'paypal' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PayPal Email
                    </label>
                    <input
                      type="email"
                      value={withdrawAddress}
                      onChange={(e) => setWithdrawAddress(e.target.value)}
                      className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your PayPal email"
                      disabled={isProcessing}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Limit: {withdrawMethods.paypal.limit}
                      <br />
                      Fee: {withdrawMethods.paypal.fee}
                    </p>
                  </div>
                )}
  
                {/* Email Notification Option */}
                <div className="border-t pt-4">
                  <div className="flex items-center mb-4">
                    <input
                      id="notifyByEmail"
                      type="checkbox"
                      checked={notifyByEmail}
                      onChange={(e) => setNotifyByEmail(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                      disabled={isProcessing}
                    />
                    <label htmlFor="notifyByEmail" className="ml-2 text-sm text-gray-700">
                      Notify me about withdrawal status by email
                    </label>
                  </div>
                  
                  {notifyByEmail && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email for notifications
                      </label>
                      <input
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your email address"
                        disabled={isProcessing}
                      />
                    </div>
                  )}
                </div>
  
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      isProcessing 
                        ? 'bg-primary-400 cursor-not-allowed' 
                        : 'bg-primary-600 hover:bg-primary-700'
                    } text-white`}
                    whileHover={isProcessing ? {} : { scale: 1.02 }}
                    whileTap={isProcessing ? {} : { scale: 0.98 }}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Request Withdrawal'
                    )}
                  </motion.button>
                  <p className="mt-2 text-sm text-gray-500 text-center">
                    Withdrawals are processed within 24 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DashboardPanel = () => {
    // Calculate days remaining for each active investment
    const activeInvestments = userData?.investments?.filter(inv => inv.status === 'active') || [];
    
    // Get the most recent active investment
    const latestInvestment = activeInvestments.length > 0 
      ? activeInvestments.reduce((latest, current) => {
          const currentDate = new Date(current.startDate);
          const latestDate = new Date(latest.startDate);
          return currentDate > latestDate ? current : latest;
        }, activeInvestments[0])
      : null;

    // Calculate days remaining and progress for the latest investment
    const getDaysRemaining = (investment) => {
      if (!investment) return 0;
      const startDate = new Date(investment.startDate);
      const endDate = new Date(investment.endDate);
      const now = new Date();
      const totalDays = 21;
      const elapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
      return Math.max(0, totalDays - elapsed);
    };

    const getProgress = (investment) => {
      if (!investment) return 0;
      const totalDays = 21;
      const remaining = getDaysRemaining(investment);
      return ((totalDays - remaining) / totalDays) * 100;
    };

    return (
      <div className="space-y-8 pb-8">
        {/* Active Investment Plan */}
        {latestInvestment ? (
          <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg shadow-lg p-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-white mb-4 md:mb-0">
                <h2 className="text-2xl font-bold mb-2">{latestInvestment.planName}</h2>
                <p className="text-white/80">Investment Amount: ${parseFloat(latestInvestment.amount).toLocaleString()}</p>
                <p className="text-white/80">Expected Return: ${parseFloat(latestInvestment.expectedReturn).toLocaleString()}</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-white/10 rounded-lg p-4 text-center min-w-[120px] mb-3">
                  <span className="text-3xl font-bold text-white">{getDaysRemaining(latestInvestment)}</span>
                  <p className="text-sm text-white/80 mt-1">Days Remaining</p>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div 
                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${getProgress(latestInvestment)}%` }}
                  />
                </div>
                <p className="text-sm text-white/80 mt-2">
                  {Math.round(getProgress(latestInvestment))}% Complete
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Investment Plan</h2>
            <p className="text-gray-600 mb-4">Start your investment journey today!</p>
            <button
              onClick={() => window.open('/investment-plans', '_blank')}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              View Investment Plans
            </button>
          </div>
        )}

        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white/90 font-medium">Total Balance</h3>
              <BanknotesIcon className="h-6 w-6 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white">
              ${userData?.balance?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-white/80 mt-2">Available for withdrawal</p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white/90 font-medium">Total Invested</h3>
              <ChartBarIcon className="h-6 w-6 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white">
              ${userData?.totalInvested?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-white/80 mt-2">Current investments</p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white/90 font-medium">Total Profit</h3>
              <ArrowTrendingUpIcon className="h-6 w-6 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white">
              ${userData?.totalProfit?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-white/80 mt-2">Total earnings</p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white/90 font-medium">Active Investments</h3>
              <CircleStackIcon className="h-6 w-6 text-white/80" />
            </div>
            <p className="text-3xl font-bold text-white">
              {userData?.investments?.length || '0'}
            </p>
            <p className="text-sm text-white/80 mt-2">Running plans</p>
          </motion.div>
        </div>
        
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <motion.button
            onClick={() => setActiveTab('deposit')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-green-100 p-3 rounded-full mb-3">
              <ArrowUpCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-sm font-semibold">Deposit</h3>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab('withdraw')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-red-100 p-3 rounded-full mb-3">
              <ArrowDownCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-sm font-semibold">Withdraw</h3>
          </motion.button>

          <motion.button
            onClick={() => window.open('/investment-plans', '_blank')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-blue-100 p-3 rounded-full mb-3">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-sm font-semibold">Invest</h3>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab('notifications')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center relative"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-yellow-100 p-3 rounded-full mb-3">
              <BellIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-sm font-semibold">Alerts</h3>
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </motion.button>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6">
          {/* Portfolio Performance */}
          <div className="bg-white rounded-lg shadow-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Portfolio Performance</h2>
            <PortfolioPerformance 
              portfolioData={portfolioData}
              performanceData={performanceData}
              userData={userData}
            />
          </div>

          {/* Trading Overview */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Market Overview</h2>
                  <p className="text-white/80 mt-1">Real-time cryptocurrency market data</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center px-3 py-1 bg-white/10 rounded-full">
                    <span className="text-sm text-white mr-2">Live Data</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="text-white text-sm font-semibold">
                    Powered by TradingView
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 p-6">
              {/* Market Summary */}
              <div className="space-y-6">
                {/* Market Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-600">Market Cap</h3>
                      <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">$2.89T</p>
                    <p className="text-sm text-green-600 mt-1">+2.4%</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-600">24h Volume</h3>
                      <ChartBarIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">$98.7B</p>
                    <p className="text-sm text-blue-600 mt-1">+5.8%</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-600">BTC Dominance</h3>
                      <CircleStackIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">48.2%</p>
                    <p className="text-sm text-purple-600 mt-1">-0.3%</p>
                  </div>
                </div>

                {/* Advanced Chart */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Advanced Trading Chart</h3>
                      <p className="text-sm text-gray-600">Interactive multi-timeframe analysis</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>Professional Tools</span>
                      <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200">
                    <TradingViewWidget />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Recent Activity</h2>
              <button className="text-primary-600 text-sm font-medium hover:text-primary-700">
                View All
              </button>
            </div>
            <div className="space-y-3">
              {userData?.transactions?.slice(0, 5).map((transaction, index) => (
                <motion.div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${
                      transaction.type === 'deposit' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {transaction.type === 'deposit' ? (
                        <ArrowUpCircleIcon className="h-6 w-6 text-green-600" />
                      ) : (
                        <ArrowDownCircleIcon className="h-6 w-6 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {transaction.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(transaction.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'deposit' ? '+' : '-'}${transaction.amount.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">{transaction.status}</p>
                  </div>
                </motion.div>
              ))}
              {(!userData?.transactions || userData.transactions.length === 0) && (
                <div className="text-center text-gray-500 py-3">
                  No recent activity
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading || error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        {loading ? (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        ) : (
          <div className="text-red-600 text-center p-4">
            <p className="text-xl font-semibold">Error loading dashboard</p>
            <p className="mt-2">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* Increase opacity and adjust the background pattern */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundColor: '#f8fafc',
          backgroundImage: `
            linear-gradient(30deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(150deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(30deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(150deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(60deg, #0ea5e9 25%, transparent 25.5%, transparent 75%, #0ea5e9 75%, #0ea5e9),
            linear-gradient(60deg, #0ea5e9 25%, transparent 25.5%, transparent 75%, #0ea5e9 75%, #0ea5e9)
          `,
          backgroundSize: '80px 140px',
          backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px',
          opacity: 0.15  // Increased opacity
        }}
      />

      {/* Remove any bg-white or bg-gray-50 from the main container */}
      <div className="min-h-screen">
        <TopNav />
        <Sidebar />
        
        <div className="lg:ml-64 relative z-10">
          <div className="pt-16 lg:pt-8 px-4 lg:p-8">
            <div className="max-w-7xl mx-auto">
              {activeTab === 'dashboard' && <DashboardPanel />}
              {activeTab === 'deposit' && <DepositPanel />}
              {activeTab === 'withdraw' && <WithdrawPanel />}
              {activeTab === 'notifications' && <NotificationsPanel />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Updated StatCard Component to handle subtext and icon
const StatCard = ({ title, value, color, subtext, icon }) => (
  <div className={`bg-gradient-to-r ${color} rounded-lg shadow-lg p-3 lg:p-6 text-white`}>
    <div className="flex items-center justify-between">
      <h3 className="text-xs lg:text-sm text-white/80">{title}</h3>
      {icon && <div>{icon}</div>}
    </div>
    <p className="text-lg lg:text-2xl font-bold mt-1 lg:mt-2">{value}</p>
    {subtext && (
      <p className="text-xs lg:text-sm mt-1 lg:mt-2 text-white/80">
        {subtext}
      </p>
    )}
  </div>
);

export default UserDashboard; 